/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { CosmosTagProps, CosmosText, TagList } from "@components/elements";
import type { BlogTagFragment } from "@cosmos/hygraph-schema";
import { useTranslation } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";

const ATTRIBUTION_POSITION = 16;
const STACK_GAP = 16;
const TAG_GAP = 8;
const MORE_TAG_CHAR_WIDTH = 12; // Approximate pixel width per character for estimating "+ X more" tag width
const MORE_TAG_PADDING_INLINE = 24; // eds.dimension.12 × 2 sides

const TagAttributionStackStyle: string = css({
	position: "absolute",
	insetBlockEnd: `eds.dimension.${ATTRIBUTION_POSITION}`,
	insetInlineEnd: `eds.dimension.${ATTRIBUTION_POSITION}`,
	width: `calc(100% - (2 * token(spacing.eds.dimension.${ATTRIBUTION_POSITION})))`,
});

const TagListStyle: string = css({
	"&&": {
		display: "flex",
		flexWrap: "nowrap",
		gap: `eds.dimension.${TAG_GAP}`,
		flexShrink: 0,
		justifySelf: "flex-start",
		minHeight: "eds.dimension.24",
		overflow: "hidden",
		"& .tag": {
			zIndex: "2",
			flexShrink: 0,
			backdropFilter: "blur(calc(75px / 2))",
			backgroundColor: "eds.material.300",
			paddingBlock: "0",
			"& svg": {
				width: "eds.dimension.16!",
				height: "eds.dimension.16!",
			},
		},
	},
});

const AttributionStyle: string = css({
	minHeight: "eds.dimension.24",
	paddingBlock: "eds.dimension.3",
	paddingInline: "eds.dimension.8",
	borderRadius: "eds.borderRadius.4",
	borderEndEndRadius: `calc(var(--asset-border-radius, 20px) - ${ATTRIBUTION_POSITION}px) !important`,
	wordWrap: "anywhere",

	marginInlineStart: "auto",
	alignItems: "center",
	textShadow: "0px 2px 4px rgba(0, 0, 0, 0.20)",

	color: "eds.text.contrast",
	bgColor: "rgba(48, 48, 52, 0.4)",
	backdropFilter: "blur(50px)",
	lineClamp: "3",
	overflow: "hidden",
	mdDown: {
		lineClamp: "2",
	},
});

export type TagAttributionStackProps = {
	attribution?: string | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	className?: string | null;
	disableMoreTag?: boolean;
};

export const TagAttributionStack = ({
	attribution,
	tags,
	className,
	disableMoreTag,
}: TagAttributionStackProps): React.JSX.Element | null => {
	const containerRef = useRef<HTMLDivElement>(null);
	const tagWidthsRef = useRef<number[]>([]);
	// null = measurement phase (render all tags so we can measure them)
	// number = display phase (render this many tags)
	const [visibleCount, setVisibleCount] = useState<number | null>(null);
	const [measureRetry, setMeasureRetry] = useState(0);
	const tagCount = tags?.length ?? 0;
	const isDisabledMoreTag = disableMoreTag || tagCount < 3;
	const t = useTranslation();

	// t() can return string | string[] depending on interpolation; normalize to string
	const formatMoreLabel = useCallback(
		(hiddenCount: number) => [t("epic.cosmos.plus.x.more", [hiddenCount])].flat().join(""),
		[t],
	);

	const estimateMoreTagWidth = useCallback(
		(hiddenCount: number) => {
			return formatMoreLabel(hiddenCount).length * MORE_TAG_CHAR_WIDTH + MORE_TAG_PADDING_INLINE;
		},
		[formatMoreLabel],
	);

	// Measures attribution width by temporarily hiding the tag list so attribution
	// can expand to its natural width. The hide is restored before paint.
	const measureAttributionWidth = useCallback(() => {
		const container = containerRef.current;
		if (!container) return 0;
		const tagListEl = container.querySelector<HTMLElement>(".tag-list");
		const attributionEl = container.querySelector(".cosmos-attribution");
		if (!tagListEl || !attributionEl) return 0;

		const origDisplay = tagListEl.style.display;
		tagListEl.style.display = "none";
		const width = attributionEl.getBoundingClientRect().width;
		tagListEl.style.display = origDisplay;
		return width;
	}, []);

	const calculateVisibleCount = useCallback(
		(availableWidth: number) => {
			const tagWidths = tagWidthsRef.current;
			if (!tagWidths.length) return;

			// Determine how many tags fit within the available width
			let usedWidth = 0;
			let fittingCount = 0;
			for (let i = 0; i < tagWidths.length; i++) {
				const tagWidth = tagWidths[i] + (i > 0 ? TAG_GAP : 0);
				if (usedWidth + tagWidth <= availableWidth) {
					usedWidth += tagWidth;
					fittingCount++;
				} else {
					break;
				}
			}

			// All tags can fit, show them all
			if (fittingCount >= tagCount) {
				setVisibleCount(tagCount);
				return;
			}

			// Not all tags fit — remove tags from the end until the "+ X more" tag fits
			while (fittingCount > 0) {
				const moreTagWidth = estimateMoreTagWidth(tagCount - fittingCount) + TAG_GAP;
				if (usedWidth + moreTagWidth <= availableWidth) break;
				fittingCount--;
				usedWidth -= tagWidths[fittingCount] + (fittingCount > 0 ? TAG_GAP : 0);
			}

			// Always show at least one real tag before the "+ X more" tag
			setVisibleCount(Math.max(1, fittingCount));
		},
		[tagCount, estimateMoreTagWidth],
	);

	// Measures attribution width (via display:none trick) and recalculates how many tags fit
	const measureAndCalculate = useCallback(() => {
		const container = containerRef.current;
		if (!container || !tagWidthsRef.current.length) return;

		const containerWidth = container.offsetWidth;
		const attrWidth = Math.min(measureAttributionWidth(), containerWidth - STACK_GAP);
		const availableWidthForTags = containerWidth - (attrWidth > 0 ? attrWidth + STACK_GAP : 0);
		calculateVisibleCount(availableWidthForTags);
	}, [measureAttributionWidth, calculateVisibleCount]);

	const prevTagsRef = useRef(tags);
	const prevAttributionRef = useRef(attribution);
	useLayoutEffect(() => {
		if (isDisabledMoreTag || !containerRef.current || !tagCount) return;

		// Detect prop changes and invalidate cached tag widths
		if (prevTagsRef.current !== tags || prevAttributionRef.current !== attribution) {
			prevTagsRef.current = tags;
			prevAttributionRef.current = attribution;
			tagWidthsRef.current = [];
			if (visibleCount !== null) {
				setVisibleCount(null);
				return;
			}
		}

		// Measurement phase: all tags are in the DOM (visibleCount is null)
		if (visibleCount === null) {
			const tagElements = containerRef.current.querySelectorAll(".tag-list .tag");
			if (!tagElements.length) return;
			const widths = Array.from(tagElements).map((el) => el.getBoundingClientRect().width);
			// If container isn't laid out yet, ResizeObserver will retry
			if (!widths[0]) return;
			tagWidthsRef.current = widths;
		}

		measureAndCalculate();
	}, [isDisabledMoreTag, tags, attribution, visibleCount, tagCount, measureAndCalculate, measureRetry]);

	// Recalculate on observed container resize using cached tag widths + fresh attribution measurement
	useEffect(() => {
		if (isDisabledMoreTag) return;
		const container = containerRef.current;
		if (!container || !tagCount) return;

		const observer = new ResizeObserver(() => {
			if (tagWidthsRef.current.length) {
				measureAndCalculate();
			} else {
				// Measurement incomplete (e.g., container wasn't visible) — retry
				setMeasureRetry((n) => n + 1);
			}
		});
		observer.observe(container);

		// Re-measure after fonts load — initial tag widths may use fallback fonts
		if (document.fonts.status !== "loaded") {
			void document.fonts.ready.then(() => {
				tagWidthsRef.current = [];
				setVisibleCount(null);
			});
		}

		return () => observer.disconnect();
	}, [isDisabledMoreTag, measureAndCalculate, tagCount]);

	// Derive display state: use measured count if available, otherwise show all tags for measurement
	const effectiveCount = visibleCount ?? tagCount;
	const hiddenCount = tagCount - effectiveCount;
	const visibleTags = useMemo(() => {
		if (isDisabledMoreTag) return tags ?? [];
		const result = tags?.slice(0, effectiveCount) ?? [];
		// Only append the "+ X more" tag after measurement is complete (visibleCount !== null)
		if (hiddenCount > 0 && visibleCount !== null) {
			return [...result, { label: formatMoreLabel(hiddenCount) }];
		}
		return result;
	}, [isDisabledMoreTag, tags, effectiveCount, hiddenCount, visibleCount, formatMoreLabel]);

	if (!attribution && !tagCount) return null;

	return (
		<HStack
			ref={containerRef}
			className={cx("tag-attribution-stack", TagAttributionStackStyle, className)}
			gap={`eds.dimension.${STACK_GAP}`}
			justifyContent="space-between"
			alignItems="flex-end"
			overflow="hidden"
		>
			{tagCount ? <TagList tags={visibleTags} size="uiSM" className={TagListStyle} /> : null}
			<CosmosText className={cx("cosmos-attribution", AttributionStyle)} variant="uiSM" text={attribution} />
		</HStack>
	);
};
